<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { intlLocale, t } from '../i18n';
import { isPhone } from '../isPhone';
import { portfolioTotals } from '../portfolioView';
import {
  currentPriceOf, dailyPercentOf, dailyYieldOf, livePriceOf, marketValueOf, yieldAbsOf, yieldPercentOf,
} from '../positionView';
import {
  accountTypeLabel,
  formatDateTime,
  formatMoney,
  formatNumber,
  formatSignedMoney,
  formatSignedPercent,
  formatTime,
  instrumentTypeLabel,
  signClass,
  toNumber,
} from '../format';
import { readUiState, writeUiState } from '../uiState';
import {
  sectorName, sectorOf, sectorRowStyle, sectorStyle,
} from '../sectors';
import HomeActions from './HomeActions.vue';
import IisLimit from './IisLimit.vue';
import PositionSortBar from './PositionSortBar.vue';

const props = defineProps({
  portfolio: {
    type: Object,
    required: true,
  },
  busy: {
    type: Boolean,
    default: false,
  },
  canRefresh: {
    type: Boolean,
    default: true,
  },
  // uid → { price, time } from GET /portfolios/live-prices (docs/live-prices/SPEC.md)
  livePrices: {
    type: Object,
    default: () => ({}),
  },
  // Today's CBR rates { USD: 83.6, … } (docs/fx/SPEC.md, decision 6)
  fxRates: {
    type: Object,
    default: () => ({}),
  },
  // IIS deposits this year, null when off (docs/iis/SPEC.md)
  iis: {
    type: Object,
    default: null,
  },
});

// portfolio.kind: 'account' | 'capital' | 'group' (resources/js/portfolioView.js)
// open-instrument: a tap on a position opens the instrument screen (docs/instrument/SPEC.md, decision 2).
const emit = defineEmits(['refresh', 'remove', 'edit', 'navigate', 'open-instrument', 'toast']);

const isAccount = computed(() => (props.portfolio.kind ?? 'account') === 'account');
// Only on an IIS account itself, not in «Капитал» or composites (docs/iis/SPEC.md, decision 3).
const showIis = computed(() => props.iis !== null && isAccount.value && props.portfolio.type === 'ACCOUNT_TYPE_TINKOFF_IIS');

function livePrice(position) {
  return livePriceOf(position, props.livePrices);
}

function currentPrice(position) {
  return currentPriceOf(position, props.livePrices);
}

// A position's change follows the line under the total: all time or today (docs/portfolios/SPEC.md,
// decision 26). The sort by ₽ / % and the sector totals use the same values.
function yieldAbs(position) {
  return todayMode.value ? dailyYieldOf(position, props.livePrices) : yieldAbsOf(position, props.livePrices);
}

function yieldPercent(position) {
  return todayMode.value ? dailyPercentOf(position, props.livePrices) : yieldPercentOf(position, props.livePrices);
}

// Portfolio total in its currency: positions in other currencies stay on the snapshot (decision 10).
// Rate from the position's currency to the total's (RUB) at today's CBR rate; null = unknown (docs/fx/SPEC.md).
function rateOf(position) {
  if (position.currency === props.portfolio.currency) {
    return 1;
  }
  return toNumber(props.fxRates[position.currency]);
}

// Value, profit and today's change with live prices (shared with the analytics summary).
const totals = computed(() => portfolioTotals(props.portfolio, props.livePrices, props.fxRates));
const total = computed(() => totals.value.total);
const totalYieldAbs = computed(() => totals.value.yieldAbs);
const totalYieldPercent = computed(() => totals.value.yieldPercent);
const dailyYield = computed(() => totals.value.daily);
const dailyYieldPercent = computed(() => totals.value.dailyPercent);

// One line under the total: «за всё время», a tap switches to «за сегодня» and back
// (docs/design/SPEC.md, decision 33). The choice is remembered in the browser.
const showToday = ref(readUiState('home.period', 'all') === 'today');
watch(showToday, (today) => writeUiState('home.period', today ? 'today' : 'all'));
// «За сегодня» only while today's change is known (a snapshot from today), otherwise all time.
const todayMode = computed(() => showToday.value && dailyYield.value !== null);
const periodChange = computed(() => {
  const currency = props.portfolio.currency;
  if (todayMode.value) {
    return {
      sign: dailyYield.value,
      suffix: t('home.todaySuffix'),
      text: [
        formatSignedMoney(dailyYield.value, currency),
        dailyYieldPercent.value !== null ? formatSignedPercent(dailyYieldPercent.value, 2) : null,
      ].filter(Boolean).join(' · '),
    };
  }
  return {
    sign: totalYieldPercent.value,
    suffix: t('home.allTimeSuffix'),
    text: [
      totalYieldAbs.value !== null ? formatSignedMoney(totalYieldAbs.value, currency) : null,
      formatSignedPercent(totalYieldPercent.value, 2),
    ].filter(Boolean).join(' · '),
  };
});

// Logos that failed to load fall back to the letter circle.
const brokenLogos = reactive(new Set());

function initial(position) {
  const label = position.name || position.ticker || '?';
  return label.trim().charAt(0).toUpperCase();
}

function marketValue(position) {
  return marketValueOf(position, props.livePrices);
}

// Share of the portfolio total (decision 22). Only positions in the total's currency: no exchange rates.
function sharePercent(position) {
  const value = valueRub(position);
  if (value === null || position.outside_total || !total.value) {
    return null;
  }
  return (value / total.value) * 100;
}

// Value and yield in RUB; foreign positions are converted, the original stays visible in gray.
function valueRub(position) {
  const value = marketValue(position);
  const rate = rateOf(position);
  return value === null || rate === null ? null : value * rate;
}

function isConverted(position) {
  return position.currency !== props.portfolio.currency && rateOf(position) !== null;
}

// Sorting inside every group (docs/portfolios/SPEC.md, decision 23): one choice for all portfolios,
// remembered in the browser. A new key starts in its natural order, ⇅ reverses it.
const SORT_KEYS = ['value', 'name', 'yieldAbs', 'yieldPercent'];
const NATURAL_DESC = { value: true, name: false, yieldAbs: true, yieldPercent: true };
const savedSort = readUiState('home.sort', null);
const sortKey = ref(SORT_KEYS.includes(savedSort?.key) ? savedSort.key : 'value');
const sortDesc = ref(typeof savedSort?.desc === 'boolean' ? savedSort.desc : NATURAL_DESC[sortKey.value]);
watch([sortKey, sortDesc], () => writeUiState('home.sort', { key: sortKey.value, desc: sortDesc.value }));

function chooseSort(key) {
  if (sortKey.value !== key) {
    sortKey.value = key;
    sortDesc.value = NATURAL_DESC[key];
  }
}

// «По секторам» (docs/design/SPEC.md, decision 34): on/off, remembered; the sort above then orders
// the sectors inside every type and the positions inside every sector.
const bySector = ref(readUiState('home.bySector', false) === true);
watch(bySector, (on) => writeUiState('home.bySector', on));

function displayName(position) {
  return position.name || position.ticker || position.instrument_uid || '';
}

// Value and gain in RUB so foreign positions compare with rouble ones; null = unknown (always last).
function sortValue(position) {
  switch (sortKey.value) {
    case 'yieldAbs': {
      const value = yieldAbs(position);
      const rate = rateOf(position);
      return position.instrument_type === 'currency' || value === null || rate === null ? null : value * rate;
    }
    case 'yieldPercent':
      return position.instrument_type === 'currency' ? null : yieldPercent(position);
    default:
      return valueRub(position);
  }
}

function comparePositions(a, b) {
  const sign = sortDesc.value ? -1 : 1;
  if (sortKey.value === 'name') {
    return sign * displayName(a).localeCompare(displayName(b), intlLocale(), { sensitivity: 'base', numeric: true });
  }
  const x = sortValue(a);
  const y = sortValue(b);
  if (x === null || y === null) {
    return (x === null) - (y === null);
  }
  return sign * (x - y) || displayName(a).localeCompare(displayName(b), intlLocale());
}

// Sectors compare by the same key: value and gain summed in RUB, gain % of the sector's cost, name.
function sectorTotals(sector, positions) {
  const sum = (values) => {
    const known = values.filter((v) => v !== null);
    return known.length ? known.reduce((total, v) => total + v, 0) : null;
  };
  const value = sum(positions.map(valueRub));
  const gain = sum(positions.map((p) => {
    const abs = yieldAbs(p);
    const rate = rateOf(p);
    return p.instrument_type === 'currency' || abs === null || rate === null ? null : abs * rate;
  }));
  const percent = value !== null && gain !== null && value - gain !== 0 ? (gain / (value - gain)) * 100 : null;
  return { sector, positions, name: sectorName(sector), value, gain, percent };
}

function compareSectors(a, b) {
  const sign = sortDesc.value ? -1 : 1;
  if (sortKey.value === 'name') {
    return sign * a.name.localeCompare(b.name, intlLocale(), { sensitivity: 'base' });
  }
  const pick = { value: 'value', yieldAbs: 'gain', yieldPercent: 'percent' }[sortKey.value];
  const x = a[pick];
  const y = b[pick];
  if (x === null || y === null) {
    return (x === null) - (y === null);
  }
  return sign * (x - y) || a.name.localeCompare(b.name, intlLocale());
}

// A small gap before the first position of every next sector, so neighbouring colors do not merge.
function startsSector(positions, index) {
  return bySector.value && index > 0 && sectorOf(positions[index]) !== sectorOf(positions[index - 1]);
}

function orderPositions(positions) {
  if (!bySector.value) {
    return [...positions].sort(comparePositions);
  }
  const bySectorMap = new Map();
  for (const position of positions) {
    const sector = sectorOf(position);
    if (!bySectorMap.has(sector)) {
      bySectorMap.set(sector, []);
    }
    bySectorMap.get(sector).push(position);
  }
  return [...bySectorMap.entries()]
    .map(([sector, list]) => sectorTotals(sector, list))
    .sort(compareSectors)
    .flatMap((group) => [...group.positions].sort(comparePositions));
}

// Positions grouped by instrument type in order of appearance; currency (cash) always last (decision 15).
// Inside a group the order is the chosen sort (decision 23), the header carries the group's value in RUB
// (docs/design/SPEC.md, item 5).
const groups = computed(() => {
  const byType = new Map();
  for (const position of props.portfolio.positions ?? []) {
    const type = position.instrument_type ?? 'other';
    if (!byType.has(type)) {
      byType.set(type, []);
    }
    byType.get(type).push(position);
  }
  return [...byType.entries()]
    .map(([type, positions]) => {
      const values = positions.map(valueRub).filter((v) => v !== null);
      return {
        type,
        positions: orderPositions(positions),
        value: values.length ? values.reduce((sum, v) => sum + v, 0) : null,
      };
    })
    .sort((a, b) => (a.type === 'currency') - (b.type === 'currency'));
});

// Phone: a tap on a row shows quantity, average → current price and FIFO (docs/design/SPEC.md, decision 13).
function positionValue(position) {
  return isConverted(position) ? valueRub(position) : marketValue(position);
}

function positionYield(position) {
  return isConverted(position) ? yieldAbs(position) * rateOf(position) : yieldAbs(position);
}

function positionCurrency(position) {
  return isConverted(position) ? props.portfolio.currency : position.currency;
}

// Always two decimals: "3,00 %" next to "4,75 %" (docs/design/SPEC.md, decision 5).
function shareText(position) {
  const share = sharePercent(position);
  return share === null ? null : `${new Intl.NumberFormat(intlLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(share)} %`;
}
</script>

<template>
  <!-- Phone: the total card, then all assets (docs/design/SPEC.md, «Главная», decision 13) -->
  <div
    v-if="isPhone"
    class="space-y-3"
  >
    <section class="rounded-3xl bg-slate-900 p-5">
      <p class="text-[2.25rem] font-semibold leading-10 tracking-tight tabular-nums text-slate-100">
        {{ formatMoney(total, portfolio.currency) }}
      </p>
      <p
        v-if="isAccount"
        class="mt-1 text-[0.8125rem] text-slate-500"
      >
        {{ accountTypeLabel(portfolio.type) }}
      </p>
      <button
        type="button"
        class="relative mt-2 inline-flex items-center gap-1.5 rounded text-left text-[0.9375rem] tabular-nums before:absolute before:inset-x-0 before:top-1/2 before:h-full before:min-h-11 before:-translate-y-1/2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
        :disabled="dailyYield === null"
        @click="showToday = !showToday"
      >
        <span :class="signClass(periodChange.sign)">{{ periodChange.text }}</span>
        <span class="text-slate-500">{{ periodChange.suffix }}</span>
        <svg
          v-if="dailyYield !== null"
          class="size-3 shrink-0 text-slate-500"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M7 9l5-5 5 5M7 15l5 5 5-5" />
        </svg>
        <span class="sr-only">{{ t('home.periodSwitch') }}</span>
      </button>
      <IisLimit
        v-if="showIis"
        :iis="iis"
      />
    </section>

    <HomeActions
      @navigate="(section) => emit('navigate', section)"
      @soon="(text) => emit('toast', text, 'info')"
    />

    <section class="rounded-3xl bg-slate-900 py-2">
      <p
        v-if="groups.length > 0"
        class="px-5 pt-3 text-[0.8125rem] text-slate-400"
      >
        {{ t('portfolio.gainPeriod', { period: periodChange.suffix }) }}
      </p>
      <PositionSortBar
        v-if="groups.length > 0"
        class="px-5 pb-1 pt-2"
        :keys="SORT_KEYS"
        :sort-key="sortKey"
        :desc="sortDesc"
        :by-sector="bySector"
        @choose="chooseSort"
        @reverse="sortDesc = !sortDesc"
        @toggle-sectors="bySector = !bySector"
      />
      <p
        v-if="groups.length === 0"
        class="px-5 py-3 text-[0.9375rem] text-slate-500"
      >
        {{ t('portfolio.noPositions') }}
      </p>
      <div
        v-for="group in groups"
        :key="group.type"
      >
        <h3 class="flex items-baseline justify-between gap-3 px-5 pb-1 pt-3 text-[0.8125rem] text-slate-500">
          <span>{{ instrumentTypeLabel(group.type) }}</span>
          <span
            v-if="group.value !== null"
            class="tabular-nums"
          >{{ formatMoney(group.value, portfolio.currency) }}</span>
        </h3>
        <ul>
          <li
            v-for="(position, index) in group.positions"
            :key="position.id"
            :class="{ 'mt-1': startsSector(group.positions, index) }"
          >
            <!-- First lines on one line left and right (docs/design/SPEC.md, decision 14) -->
            <button
              type="button"
              class="flex w-full items-start gap-3 px-5 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400 active:bg-slate-800"
              :style="bySector ? sectorRowStyle(sectorOf(position)) : null"
              :disabled="!position.instrument_uid"
              @click="emit('open-instrument', position.instrument_uid)"
            >
              <!-- Phone: the sector sits in the corner of the logo (docs/design/SPEC.md, decision 35) -->
              <span class="relative shrink-0">
                <img
                  v-if="position.logo_url && !brokenLogos.has(position.id)"
                  :src="position.logo_url"
                  alt=""
                  loading="lazy"
                  class="size-10 rounded-full bg-slate-800 object-cover"
                  @error="brokenLogos.add(position.id)"
                >
                <span
                  v-else
                  class="flex size-10 items-center justify-center rounded-full bg-slate-700 text-[0.8125rem] font-semibold text-white"
                  :style="position.logo_color ? { backgroundColor: position.logo_color } : null"
                  aria-hidden="true"
                >{{ initial(position) }}</span>
                <span
                  v-if="bySector"
                  class="absolute -bottom-1 -right-1 flex size-[18px] items-center justify-center rounded-full bg-slate-900 ring-2 ring-slate-900"
                  :style="{ color: sectorStyle(sectorOf(position)).color }"
                >
                  <svg
                    class="size-3"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path :d="sectorStyle(sectorOf(position)).icon" />
                  </svg>
                  <span class="sr-only">{{ sectorName(sectorOf(position)) }}</span>
                </span>
              </span>
              <span class="min-w-0 flex-1">
                <span class="flex items-baseline justify-between gap-3">
                  <span class="min-w-0 truncate text-[0.9375rem] font-medium leading-5 text-slate-100">{{ position.name || position.ticker || position.instrument_uid }}</span>
                  <span class="shrink-0 text-[0.9375rem] font-medium leading-5 tabular-nums text-slate-100">{{ formatMoney(positionValue(position), positionCurrency(position)) }}</span>
                </span>
                <span class="mt-1 flex items-baseline justify-between gap-3 text-[0.8125rem] leading-4 tabular-nums">
                  <span class="min-w-0 truncate text-slate-400">{{ [position.name ? position.ticker : null, shareText(position)].filter(Boolean).join(' · ') }}</span>
                  <span
                    v-if="yieldAbs(position) !== null && position.instrument_type !== 'currency'"
                    class="shrink-0"
                    :class="signClass(yieldAbs(position))"
                  >{{ formatSignedMoney(positionYield(position), positionCurrency(position)) }}<template v-if="yieldPercent(position) !== null"> · {{ formatSignedPercent(yieldPercent(position), 2) }}</template></span>
                </span>
              </span>
            </button>
          </li>
        </ul>
      </div>
    </section>

    <slot />
  </div>

  <!-- Desktop: total card, square buttons, positions card (docs/design/SPEC.md, decision 33) -->
  <div
    v-else
    class="space-y-3"
  >
    <section class="rounded-lg border border-slate-700 bg-slate-900/40">
      <!-- Total first and large; on a phone in a column (docs/design/SPEC.md, item 4) -->
      <header class="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between sm:p-5">
        <div class="min-w-0">
          <h3
            v-if="portfolio.kind !== 'capital'"
            class="truncate text-base font-semibold text-slate-100"
          >
            {{ portfolio.name || portfolio.account_id }}
          </h3>
          <p
            v-if="isAccount"
            class="mt-0.5 text-xs text-slate-500"
          >
            {{ accountTypeLabel(portfolio.type) }} · {{ portfolio.account_id }} · {{ t('portfolio.updated', { date: formatDateTime(portfolio.synced_at) }) }}
          </p>
          <p
            v-else
            class="mt-0.5 text-xs text-slate-500"
          >
            {{ portfolio.kind === 'group' ? t('portfolio.composite') : t('portfolio.capitalHint') }}
            · {{ t('portfolio.accountCount', { count: portfolio.account_count }) }}
            <template v-if="portfolio.synced_at">
              · {{ t('portfolio.updated', { date: formatDateTime(portfolio.synced_at) }) }}
            </template>
          </p>
        </div>
        <div class="sm:text-right">
          <p class="text-[2.125rem] font-semibold leading-[2.375rem] tabular-nums text-slate-100">
            {{ formatMoney(total, portfolio.currency) }}
          </p>
          <button
            type="button"
            class="relative mt-1.5 inline-flex items-center gap-1.5 rounded text-sm tabular-nums before:absolute before:inset-x-0 before:top-1/2 before:h-full before:min-h-11 before:-translate-y-1/2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 enabled:hover:opacity-80"
            :disabled="dailyYield === null"
            @click="showToday = !showToday"
          >
            <span :class="signClass(periodChange.sign)">{{ periodChange.text }}</span>
            <span class="text-slate-500">{{ periodChange.suffix }}</span>
            <svg
              v-if="dailyYield !== null"
              class="size-3 shrink-0 text-slate-500"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M7 9l5-5 5 5M7 15l5 5 5-5" />
            </svg>
            <span class="sr-only">{{ t('home.periodSwitch') }}</span>
          </button>
        </div>
      </header>
      <!-- Full card width under the header (docs/iis/SPEC.md, decision 8) -->
      <div
        v-if="showIis"
        class="border-t border-slate-800 p-4 sm:px-5"
      >
        <IisLimit
          compact
          :iis="iis"
        />
      </div>
    </section>

    <HomeActions
      desktop
      @navigate="(section) => emit('navigate', section)"
      @soon="(text) => emit('toast', text, 'info')"
    />

    <section class="rounded-lg border border-slate-700 bg-slate-900/40">
      <!-- Extra blocks under the totals, e.g. payouts -->
      <slot />

      <div>
        <p
          v-if="groups.length > 0"
          class="px-4 pt-3 text-xs text-slate-400 sm:px-5"
        >
          {{ t('portfolio.gainPeriod', { period: periodChange.suffix }) }}
        </p>
        <PositionSortBar
          v-if="groups.length > 0"
          compact
          class="border-b border-slate-800 px-4 py-2 sm:px-5"
          :keys="SORT_KEYS"
          :sort-key="sortKey"
          :desc="sortDesc"
          :by-sector="bySector"
          @choose="chooseSort"
          @reverse="sortDesc = !sortDesc"
          @toggle-sectors="bySector = !bySector"
        />
        <section
          v-for="group in groups"
          :key="group.type"
        >
          <h4 class="flex items-center justify-between bg-slate-900/60 px-4 py-1.5 text-xs font-medium text-slate-400 sm:px-5">
            <span>{{ instrumentTypeLabel(group.type) }}</span>
            <span
              v-if="group.value !== null"
              class="tabular-nums"
            >{{ formatMoney(group.value, portfolio.currency) }}</span>
          </h4>
          <ul>
            <!-- Two lines: name / value, ticker · share / profit (docs/design/SPEC.md, item 5) -->
            <li
              v-for="(position, index) in group.positions"
              :key="position.id"
              class="flex items-center gap-3 border-t border-slate-800/60 px-4 py-2 first:border-t-0 sm:px-5"
              :class="[
                position.instrument_uid ? 'cursor-pointer hover:bg-slate-800/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400' : '',
                { 'mt-1': startsSector(group.positions, index) },
              ]"
              :role="position.instrument_uid ? 'button' : undefined"
              :tabindex="position.instrument_uid ? 0 : undefined"
              :style="bySector ? sectorRowStyle(sectorOf(position)) : null"
              @click="position.instrument_uid && emit('open-instrument', position.instrument_uid)"
              @keydown.enter.space.prevent="position.instrument_uid && emit('open-instrument', position.instrument_uid)"
            >
              <span
                v-if="bySector"
                class="flex h-8 w-5 shrink-0 items-center justify-center"
                :style="{ color: sectorStyle(sectorOf(position)).color }"
              >
                <svg
                  class="size-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path :d="sectorStyle(sectorOf(position)).icon" />
                </svg>
                <span class="sr-only">{{ sectorName(sectorOf(position)) }}</span>
              </span>
              <img
                v-if="position.logo_url && !brokenLogos.has(position.id)"
                :src="position.logo_url"
                alt=""
                loading="lazy"
                class="size-8 shrink-0 rounded-full bg-slate-800 object-cover"
                @error="brokenLogos.add(position.id)"
              >
              <span
                v-else
                class="flex size-8 shrink-0 items-center justify-center rounded-full bg-slate-700 text-xs font-semibold text-white"
                :style="position.logo_color ? { backgroundColor: position.logo_color } : null"
                aria-hidden="true"
              >{{ initial(position) }}</span>

              <div class="min-w-0 flex-1">
                <div class="flex items-baseline justify-between gap-3">
                  <p class="min-w-0 truncate text-sm font-medium text-slate-100">
                    {{ position.name || position.ticker || position.instrument_uid }}
                    <span
                      v-if="position.blocked"
                      class="ml-1 rounded bg-amber-900/50 px-1.5 py-0.5 text-[0.625rem] font-normal text-amber-300"
                    >{{ t('portfolio.blocked') }}</span>
                    <span
                      v-if="position.outside_total"
                      class="ml-1 rounded bg-slate-800 px-1.5 py-0.5 text-[0.625rem] font-normal text-slate-400"
                      :title="t('portfolio.outsideTotalHint')"
                    >{{ t('portfolio.outsideTotal') }}</span>
                  </p>
                  <p class="shrink-0 text-sm font-medium tabular-nums text-slate-100">
                    <span
                      v-if="isConverted(position)"
                      class="mr-1 hidden text-xs font-normal text-slate-500 sm:inline"
                    >{{ formatMoney(marketValue(position), position.currency) }} ·</span>
                    {{ isConverted(position) ? formatMoney(valueRub(position), portfolio.currency) : formatMoney(marketValue(position), position.currency) }}
                  </p>
                </div>
                <div class="flex items-baseline justify-between gap-3 text-xs tabular-nums">
                  <p class="flex min-w-0 items-center gap-x-1.5 truncate text-slate-400">
                    <span v-if="position.name && position.ticker">{{ position.ticker }}</span>
                    <template v-if="sharePercent(position) !== null">
                      <span
                        v-if="position.name && position.ticker"
                        class="text-slate-600"
                      >·</span>
                      <span
                        class="text-slate-300"
                        :title="t('portfolio.shareHint')"
                      >{{ shareText(position) }}</span>
                    </template>
                    <span class="hidden items-center gap-x-1.5 sm:inline-flex">
                      <span class="text-slate-600">·</span>
                      <span>{{ t('portfolio.pcs', { n: formatNumber(position.quantity) }) }}</span>
                      <template v-if="toNumber(position.average_price)">
                        <span class="text-slate-600">·</span>
                        <span :title="t('portfolio.fifo', { price: formatMoney(position.average_price_fifo, position.currency) })">
                          {{ formatMoney(position.average_price, position.currency) }}
                        </span>
                        <span
                          class="inline-flex size-3.5 items-center justify-center rounded-full bg-slate-700 text-[0.5625rem] leading-none text-slate-300"
                          :title="t('portfolio.avgToCurrent')"
                        >&rarr;</span>
                      </template>
                      <span
                        v-if="currentPrice(position) !== null"
                        :title="livePrice(position) ? t('portfolio.livePrice', { time: formatTime(livePrice(position).time) }) : undefined"
                      >
                        {{ formatMoney(currentPrice(position), position.currency) }}
                      </span>
                    </span>
                  </p>
                  <p
                    v-if="yieldAbs(position) !== null && position.instrument_type !== 'currency'"
                    class="shrink-0"
                    :class="signClass(yieldAbs(position))"
                  >
                    {{ isConverted(position)
                      ? formatSignedMoney(yieldAbs(position) * rateOf(position), portfolio.currency)
                      : formatSignedMoney(yieldAbs(position), position.currency) }}
                    <template v-if="yieldPercent(position) !== null">
                      · {{ formatSignedPercent(yieldPercent(position), 2) }}
                    </template>
                  </p>
                </div>
              </div>
            </li>
          </ul>
        </section>
        <p
          v-if="groups.length === 0"
          class="px-5 py-4 text-sm text-slate-500"
        >
          {{ t('portfolio.noPositions') }}
        </p>
      </div>

      <footer
        v-if="canRefresh || portfolio.kind !== 'capital'"
        class="flex flex-wrap gap-2 border-t border-slate-800 px-4 py-3 sm:px-5"
      >
        <button
          v-if="canRefresh"
          type="button"
          class="rounded bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700 disabled:opacity-50"
          :disabled="busy"
          @click="emit('refresh', portfolio)"
        >
          {{ t('portfolio.refresh') }}
        </button>
        <button
          v-if="portfolio.kind === 'group'"
          type="button"
          class="rounded bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700 disabled:opacity-50"
          :disabled="busy"
          @click="emit('edit', portfolio)"
        >
          {{ t('portfolio.edit') }}
        </button>
        <button
          v-if="portfolio.kind !== 'capital'"
          type="button"
          class="rounded bg-rose-900/60 px-3 py-1.5 text-sm text-rose-200 hover:bg-rose-800/60 disabled:opacity-50"
          :disabled="busy"
          @click="emit('remove', portfolio)"
        >
          {{ isAccount ? t('portfolio.remove') : t('portfolio.removeGroup') }}
        </button>
      </footer>
    </section>
  </div>
</template>
